import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BarChart3, Briefcase, Building2, CheckCircle2, ChevronDown, Cpu,
  FileText, Globe, GraduationCap, Handshake, Headphones, LayoutDashboard, Loader2,
  MapPin, Megaphone, MessageCircle, Network, Package, Rocket, Search, Store,
  Truck, Users, Wallet, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/5025635/pexels-photo-5025635.jpeg?auto=compress&cs=tinysrgb&w=1920";
const ADV_IMG = "https://images.pexels.com/photos/8112186/pexels-photo-8112186.jpeg?auto=compress&cs=tinysrgb&w=1200";

const MODELS = [
  { key: "franchise", badge: "Full Hub Setup", title: "Franchise Partner", icon: Store,
    desc: "For entrepreneurs who want to build an IndiaFast-branded courier business in their city.",
    points: ["IndiaFast branded hub", "Full booking operations", "Highest growth potential"], cta: "Explore Franchise" },
  { key: "associate", badge: "Existing Business", title: "Business Associate", icon: Building2,
    desc: "For existing businesses that want to add courier and logistics services to their offering.",
    points: ["Add courier services", "Serve more customers", "No business disruption"], cta: "Become an Associate" },
  { key: "agent", badge: "Retail & Booking", title: "Agent / Booking Partner", icon: Package,
    desc: "For businesses and individuals interested in booking domestic and international shipments.",
    points: ["Quick portal access", "Per-booking earnings", "Domestic + international"], cta: "Become an Agent" },
  { key: "local", badge: "Tier 2 / 3 Focus", title: "Local / Small-Town Partner", icon: MapPin,
    desc: "For entrepreneurs who want to build a courier business in their local market.",
    points: ["Local market focus", "Doorstep pickup network", "Regional support"], cta: "Start Locally" },
];

const WHY = [
  { icon: Globe, title: "Domestic & International Network", desc: "Access business opportunities across domestic and international courier services from one platform." },
  { icon: Truck, title: "Multiple Courier Partners", desc: "Leverage IndiaFast's courier partner network to offer suitable shipping options for every shipment." },
  { icon: Cpu, title: "Smart Technology", desc: "Use IndiaFast technology for booking, shipment management, tracking and operational visibility." },
  { icon: Briefcase, title: "Business Support", desc: "Receive guidance and operational support to help you build and manage your courier business." },
  { icon: Megaphone, title: "Marketing Support", desc: "Professional branding and marketing resources to help you acquire and serve customers." },
  { icon: Headphones, title: "Dedicated Assistance", desc: "Direct access to IndiaFast support for your day-to-day business requirements." },
];

const ROADMAP = [
  { icon: Handshake, title: "Brand", desc: "Professional IndiaFast branding and business identity." },
  { icon: Cpu, title: "Technology", desc: "Partner tools for booking, tracking and shipment management." },
  { icon: Network, title: "Courier Network", desc: "Access to domestic and international courier services." },
  { icon: GraduationCap, title: "Training", desc: "Guidance to understand the business and operating process." },
  { icon: Wrench, title: "Operations", desc: "Operational assistance for smooth shipment handling." },
  { icon: Megaphone, title: "Marketing", desc: "Professional marketing resources and promotional support." },
  { icon: Headphones, title: "Support", desc: "Ongoing assistance for all partner requirements." },
];

const STEPS = [
  { n: "01", title: "Apply", desc: "Submit your partnership enquiry with your business details." },
  { n: "02", title: "Connect", desc: "IndiaFast team contacts you and understands your business requirements." },
  { n: "03", title: "Verification", desc: "Business and location details are reviewed." },
  { n: "04", title: "Setup", desc: "Complete branding, technology and operational setup." },
  { n: "05", title: "Training", desc: "Understand IndiaFast systems and operating processes." },
  { n: "06", title: "Launch & Grow", desc: "Start serving customers and grow your local courier business." },
];

const BENEFITS = [
  "Domestic & international shipping opportunities",
  "Professional brand presence",
  "Technology-enabled operations",
  "Multiple courier service options",
  "Customer tracking",
  "Operational support",
  "Marketing assistance",
  "Scalable business model",
];

const TECH = [
  { icon: Package, title: "Shipment Booking" }, { icon: Search, title: "Shipment Tracking" },
  { icon: Users, title: "Customer Management" }, { icon: LayoutDashboard, title: "Partner Dashboard" },
  { icon: BarChart3, title: "Reports & Analytics" }, { icon: Wallet, title: "Rate Management" },
  { icon: FileText, title: "Shipment Status" }, { icon: Rocket, title: "Business Performance" },
];

const OPPORTUNITIES = [
  { icon: Truck, title: "Domestic Courier", desc: "Documents, parcels and express shipping across India." },
  { icon: Globe, title: "International Courier", desc: "Worldwide delivery to 200+ countries and territories." },
  { icon: Package, title: "NRI Shipments", desc: "Food items, gifts and personal shipments for overseas Indians." },
  { icon: Store, title: "eCommerce Shipments", desc: "Shipping solutions for online sellers and marketplaces." },
  { icon: Building2, title: "Corporate Shipments", desc: "Recurring business logistics for offices and enterprises." },
  { icon: Network, title: "B2B Logistics", desc: "Business-to-business cargo and bulk movement opportunities." },
];

const PROFILES = [
  "Existing Courier Agents", "Logistics Businesses", "Entrepreneurs", "Small Business Owners",
  "Retail Businesses", "eCommerce Service Providers", "Local & Small-Town Entrepreneurs", "Business Investors",
];

const FAQS = [
  { q: "Who can become an IndiaFast partner?", a: "Courier agents, logistics businesses, entrepreneurs, retail and small business owners, eCommerce service providers and business investors can all apply. If you have the drive to build a local courier business, there is a model for you." },
  { q: "What partnership models are available?", a: "IndiaFast offers four models: Franchise Partner, Business Associate, Agent / Booking Partner, and Local / Small-Town Partner. Each is designed for a different business stage and investment comfort." },
  { q: "Can an existing courier business become an IndiaFast partner?", a: "Yes. The Business Associate model is built for existing courier, cargo or transport businesses that want to add IndiaFast's domestic and international services to their current setup." },
  { q: "Do partners receive training?", a: "Yes. Partner onboarding includes training on the IndiaFast portal, shipment booking, documentation, packing standards and day-to-day operating processes." },
  { q: "Does IndiaFast provide technology support?", a: "Yes. Partners get access to the IndiaFast technology platform covering booking, tracking, rate management, reports and customer management, along with ongoing support." },
  { q: "What courier services can partners offer?", a: "Partners can offer domestic courier services across India and international courier services to 200+ countries through IndiaFast's courier partner network." },
  { q: "Can I operate in my local area?", a: "Yes. IndiaFast partnership models cover cities, towns and local markets. Share your preferred location in the enquiry form and the team will discuss the options for your area." },
  { q: "How do I apply?", a: "Fill in the partnership enquiry form on this page. The IndiaFast partnership team will contact you to understand your requirements and take you through the next steps." },
  { q: "What documents are required?", a: "Standard identity, address and business proofs are typically required. The exact document checklist is shared with you during the verification step." },
  { q: "How does the verification process work?", a: "After you apply, the team reviews your business profile and proposed location. Once verification is complete, you move to branding, technology setup and training before launch." },
];

const emptyForm = { name: "", phone: "", email: "", city: "", state: "", current_business: "", preferred_model: "", investment_range: "", experience: "", preferred_location: "", message: "" };
const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-saffron focus:ring-2 focus:ring-saffron/20";

export default function PartnerWithUs() {
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "Partner With IndiaFast | Courier & Logistics Business Partnership";
    const desc = "Partner with IndiaFast to build and grow your domestic and international courier business with technology, courier network access, operational support and professional business assistance.";
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", desc);
    setMeta("property", "og:title", "Partner With IndiaFast | Courier & Logistics Business Partnership");
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", "https://www.indiafast.in/partner-with-us");
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "partner-faq-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(ld);
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
    return () => document.getElementById("partner-faq-jsonld")?.remove();
  }, []);

  const scrollTo = (id) => (e) => { e?.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const applyFor = (model) => (e) => { setForm((f) => ({ ...f, preferred_model: model })); scrollTo("enquiry-form")(e); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waNum = (contact?.phone || "").replace(/\D/g, "").slice(-10);
  const waLink = waNum ? `https://wa.me/91${waNum}?text=${encodeURIComponent("Hi IndiaFast, I am interested in a courier partnership. Please share the details.")}` : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/partner-enquiry", form);
      setDone(true);
      window.scrollTo({ top: document.getElementById("enquiry-form")?.offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-page">
      <Navbar />

      {/* 1 — HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="partner-hero">
        <img src={HERO_IMG} alt="IndiaFast courier operations" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-30" aria-hidden="true">
          <line x1="12%" y1="30%" x2="38%" y2="55%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="38%" y1="55%" x2="68%" y2="25%" stroke="#ffffff" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="68%" y1="25%" x2="88%" y2="60%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <circle cx="12%" cy="30%" r="4" fill="#f97316" className="network-pulse" />
          <circle cx="38%" cy="55%" r="4" fill="#ffffff" className="network-pulse" style={{ animationDelay: "0.6s" }} />
          <circle cx="68%" cy="25%" r="4" fill="#f97316" className="network-pulse" style={{ animationDelay: "1.2s" }} />
          <circle cx="88%" cy="60%" r="4" fill="#16a34a" className="network-pulse" style={{ animationDelay: "1.8s" }} />
        </svg>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Partner With IndiaFast</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Build Your Courier Business With <span className="text-saffron">IndiaFast</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Partner with IndiaFast and unlock a powerful domestic &amp; international courier network, technology,
            operational support and business opportunities designed to help you grow.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
            <a href="#enquiry-form" onClick={scrollTo("enquiry-form")} data-testid="hero-primary-cta"
               className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
              Become a Partner <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#partnership-models" onClick={scrollTo("partnership-models")} data-testid="hero-secondary-cta"
               className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
              Explore Partnership Models
            </a>
          </div>
        </div>
      </section>

      {/* 2 — TRUST STRIP */}
      <section className="border-b border-slate-200 bg-white" data-testid="trust-strip">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-slate-100 px-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-5 lg:divide-x">
          {[
            ["Domestic & International", "Courier Services"],
            ["200+ Countries", "Global Coverage"],
            ["Multiple Courier", "Partners"],
            ["Technology-Driven", "Operations"],
            ["Dedicated Partner", "Support"],
          ].map(([v, l], i) => (
            <div key={i} className="px-4 py-6 text-center" data-testid={`trust-item-${i}`}>
              <p className="font-heading text-sm font-extrabold text-navy sm:text-base">{v}</p>
              <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-saffron">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3 — WHY PARTNER */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="why-partner-section">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Why Partner With IndiaFast</p>
        <h2 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          More Than a Courier Partnership. A Complete Business Ecosystem.
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {WHY.map((c, i) => (
            <div key={i} data-testid={`why-card-${i}`}
                 className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
                <c.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-navy">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4 — PARTNERSHIP MODELS */}
      <section id="partnership-models" className="scroll-mt-24 bg-white py-16 sm:py-20 lg:py-24" data-testid="models-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Partnership Models</p>
          <h2 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            Choose the Partnership That Fits Your Business
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {MODELS.map((m) => (
              <div key={m.key} data-testid={`model-card-${m.key}`}
                   className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-xl">
                <span className="w-fit rounded-full border border-saffron/30 bg-saffron/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-saffron">{m.badge}</span>
                <m.icon className="mt-4 h-8 w-8 text-navy" />
                <h3 className="mt-3 font-heading text-lg font-bold text-navy">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{m.desc}</p>
                <ul className="mt-4 space-y-2">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-xs font-medium text-slate-600">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {p}
                    </li>
                  ))}
                </ul>
                <button onClick={applyFor(m.key)} data-testid={`model-${m.key}-cta`}
                        className="mt-6 flex items-center justify-center gap-1.5 rounded-full border border-navy/20 px-4 py-2.5 text-xs font-bold text-navy transition-colors hover:border-saffron hover:bg-saffron hover:text-white">
                  {m.cta} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — WHAT YOU GET ROADMAP */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="roadmap-section">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">What You Get With IndiaFast</p>
        <h2 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          Everything You Need to Run a Courier Business
        </h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-7">
          {ROADMAP.map((r, i) => (
            <div key={i} data-testid={`roadmap-step-${i}`} className="relative rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm">
              <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-navy text-saffron"><r.icon className="h-5 w-5" /></span>
              <p className="mt-3 font-heading text-sm font-bold text-navy">{r.title}</p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">{r.desc}</p>
              {i < ROADMAP.length - 1 && <span className="absolute -right-2.5 top-1/2 hidden h-px w-5 bg-saffron/50 lg:block" />}
            </div>
          ))}
        </div>
      </section>

      {/* 6 — HOW IT WORKS */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">How It Works</p>
          <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            From Enquiry to Launch in 6 Steps
          </h2>
          <div className="relative mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-6">
            <span className="absolute left-0 right-0 top-6 hidden h-0.5 bg-gradient-to-r from-saffron via-slate-200 to-emerald-600 lg:block" />
            {STEPS.map((s, i) => (
              <div key={i} className="relative flex gap-4 sm:flex-col sm:gap-0" data-testid={`how-step-${i}`}>
                <span className="relative z-10 grid h-12 w-12 shrink-0 place-items-center rounded-full bg-navy font-heading text-sm font-extrabold text-saffron ring-4 ring-white">
                  {s.n}
                </span>
                <div className="sm:mt-4">
                  <p className="font-heading text-base font-bold text-navy">{s.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — ADVANTAGE SPLIT */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="advantage-section">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="relative overflow-hidden rounded-3xl lg:col-span-5">
            <img src={ADV_IMG} alt="Business partnership handshake" loading="lazy" className="h-72 w-full object-cover sm:h-96 lg:h-[480px]" />
            <span className="tricolor-beam absolute inset-x-0 bottom-0 h-1.5" />
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">The IndiaFast Advantage</p>
            <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Why Entrepreneurs Choose IndiaFast
            </h2>
            <div className="mt-7 grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
              {BENEFITS.map((b, i) => (
                <p key={i} className="flex items-start gap-2.5 text-sm font-medium text-slate-700" data-testid={`advantage-point-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> {b}
                </p>
              ))}
            </div>
            <a href="#enquiry-form" onClick={scrollTo("enquiry-form")} data-testid="advantage-cta"
               className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-express">
              Talk to Our Partnership Team <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* 8 — TECHNOLOGY (dark) */}
      <section className="relative overflow-hidden bg-navy py-16 sm:py-20 lg:py-24" data-testid="tech-section">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-saffron/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Technology For Partners</p>
          <h2 className="mt-3 max-w-2xl font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Technology That Helps You Run Smarter
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {TECH.map((t, i) => (
              <div key={i} data-testid={`tech-card-${i}`}
                   className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-colors hover:border-saffron/40 hover:bg-white/10">
                <t.icon className="h-6 w-6 text-saffron" />
                <p className="mt-3 font-heading text-sm font-bold text-white">{t.title}</p>
                <span className="mt-3 block h-1 w-10 rounded-full bg-gradient-to-r from-saffron to-emerald-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9 — BUSINESS OPPORTUNITIES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="opportunities-section">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Business Opportunities</p>
        <h2 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          One Partnership. Multiple Revenue Opportunities.
        </h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {OPPORTUNITIES.map((o, i) => (
            <div key={i} data-testid={`opp-card-${i}`}
                 className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-emerald-600/10 text-emerald-600"><o.icon className="h-5 w-5" /></span>
              <div>
                <h3 className="font-heading text-base font-bold text-navy">{o.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{o.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 10 — WHO CAN PARTNER */}
      <section className="bg-white py-16 sm:py-20" data-testid="profiles-section">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Who Can Become a Partner?</p>
          <h2 className="mx-auto mt-3 max-w-2xl font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            If You Have the Drive, IndiaFast Has the Network.
          </h2>
          <div className="mx-auto mt-9 flex max-w-4xl flex-wrap justify-center gap-3">
            {PROFILES.map((p, i) => (
              <span key={i} data-testid={`profile-chip-${i}`}
                    className="rounded-full border border-slate-200 bg-slate-50 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-saffron/40 hover:bg-saffron/10 hover:text-saffron">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — ENQUIRY FORM */}
      <section id="enquiry-form" className="scroll-mt-24 bg-slate-50 py-16 sm:py-20 lg:py-24" data-testid="enquiry-form-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="tricolor-beam h-1.5" />
            <div className="p-6 sm:p-10">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Partner Enquiry</p>
              <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
                Start Your IndiaFast Partnership Journey
              </h2>
              {done ? (
                <div className="mt-8 text-center" data-testid="partner-form-success">
                  <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-600/10"><CheckCircle2 className="h-9 w-9 text-emerald-600" /></span>
                  <p className="mt-5 font-heading text-lg font-bold text-navy">Thank you for your interest in partnering with IndiaFast.</p>
                  <p className="mt-2 text-sm text-slate-500">Our partnership team will contact you shortly.</p>
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noreferrer" data-testid="whatsapp-chat-button"
                       className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-500">
                      <MessageCircle className="h-4 w-4" /> Chat With IndiaFast
                    </a>
                  )}
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="partner-form">
                  <input required className={inputCls} data-testid="partner-input-name" placeholder="Full Name *" value={form.name} onChange={set("name")} />
                  <input required className={inputCls} data-testid="partner-input-phone" placeholder="Mobile Number *" value={form.phone} onChange={set("phone")} />
                  <input type="email" className={inputCls} data-testid="partner-input-email" placeholder="Email Address" value={form.email} onChange={set("email")} />
                  <input required className={inputCls} data-testid="partner-input-city" placeholder="City *" value={form.city} onChange={set("city")} />
                  <input required className={inputCls} data-testid="partner-input-state" placeholder="State *" value={form.state} onChange={set("state")} />
                  <input className={inputCls} data-testid="partner-input-business" placeholder="Current Business (if any)" value={form.current_business} onChange={set("current_business")} />
                  <select required className={inputCls} data-testid="partner-input-model" value={form.preferred_model} onChange={set("preferred_model")}>
                    <option value="">Preferred Partnership Model *</option>
                    {MODELS.map((m) => <option key={m.key} value={m.key}>{m.title}</option>)}
                  </select>
                  <select className={inputCls} data-testid="partner-input-investment" value={form.investment_range} onChange={set("investment_range")}>
                    <option value="">Investment Range</option>
                    <option>Under ₹50,000</option><option>₹50,000 – ₹2 Lakh</option>
                    <option>₹2 – ₹5 Lakh</option><option>₹5 Lakh+</option><option>Prefer to discuss</option>
                  </select>
                  <select className={inputCls} data-testid="partner-input-experience" value={form.experience} onChange={set("experience")}>
                    <option value="">Business Experience</option>
                    <option>First-time entrepreneur</option><option>1–3 years</option>
                    <option>3–5 years</option><option>5+ years</option>
                  </select>
                  <input className={inputCls} data-testid="partner-input-location" placeholder="Preferred Location / Area" value={form.preferred_location} onChange={set("preferred_location")} />
                  <textarea rows={3} className={`${inputCls} sm:col-span-2`} data-testid="partner-input-message" placeholder="Message — tell us about your plans" value={form.message} onChange={set("message")} />
                  <button disabled={busy} data-testid="enquiry-form-submit-button"
                          className="flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60 sm:col-span-2">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Submit Partnership Enquiry
                  </button>
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noreferrer" data-testid="whatsapp-chat-button"
                       className="flex items-center justify-center gap-2 text-sm font-bold text-emerald-600 hover:text-emerald-500 sm:col-span-2">
                      <MessageCircle className="h-4 w-4" /> Chat With IndiaFast on WhatsApp
                    </a>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 12 — FAQ */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-saffron">FAQs</p>
          <h2 className="mt-3 text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
            Partnership Questions, Answered
          </h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-xl border border-slate-200" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-toggle-${i}`}
                        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-slate-50">
                  <span className="text-sm font-bold text-navy">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="border-t border-slate-100 px-5 py-4 text-sm leading-relaxed text-slate-500">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 13 — FINAL CTA */}
      <section className="relative overflow-hidden bg-navy py-20 sm:py-24" data-testid="final-cta-section">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-emerald-600/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-saffron/15 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Your Local Business. <span className="text-saffron">IndiaFast's</span> Global Network.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">
            Take the next step toward building a stronger courier and logistics business with IndiaFast.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
            <a href="#enquiry-form" onClick={scrollTo("enquiry-form")} data-testid="final-cta-apply"
               className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
              Become a Partner <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <Link to="/support" data-testid="final-cta-talk"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">
              Talk to Our Team
            </Link>
          </div>
        </div>
      </section>

      <Footer contact={contact} />

      {/* Sticky mobile CTA */}
      {!done && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur lg:hidden" data-testid="sticky-mobile-cta">
          <a href="#enquiry-form" onClick={scrollTo("enquiry-form")}
             className="flex items-center justify-center gap-2 rounded-full bg-saffron py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30">
            Become a Partner <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  );
}
